import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Typography, 
  TextField,
  IconButton,
  InputAdornment,
  Paper,
  styled,
  useTheme,
  Avatar,
  Fade,
  Grow,
  CircularProgress,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChatIcon from '@mui/icons-material/Chat';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ConfidenceRating from './ConfidenceRating';
import { languages, useLanguage } from '../data/languages';

const QuestionContainer = styled(Box)(({ theme }) => ({
  height: '100%',
  // display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  padding: theme.spacing(3),
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(10),
  overflow: 'hidden',
  backgroundColor: 'rgba(250, 250, 252, 0.8)',
}));

const ConversationContainer = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  paddingBottom: theme.spacing(14),
  width: '100%',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.05)',
    borderRadius: '10px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.15)',
    borderRadius: '10px',
    '&:hover': {
      background: 'rgba(0, 0, 0, 0.25)',
    },
  },
  scrollbarWidth: 'thin',
  msOverflowStyle: 'none',
}));

const QuestionBlock = styled(Paper)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  padding: theme.spacing(0),
  backgroundColor: 'transparent',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(3),
  maxWidth: '900px',
  margin: '0 auto',
  width: '100%',
  position: 'relative',
  zIndex: 1,
  height: '100%',
  overflow: 'hidden',
  boxShadow: 'none',
}));

const MessageGroup = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  width: '100%',
  marginBottom: theme.spacing(1),
  position: 'relative',
}));

const MessageAvatar = styled(Avatar)(({ theme }) => ({
  width: 48,
  height: 48,
  backgroundColor: theme.palette.primary.main,
  flexShrink: 0,
  boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
}));

const ReaderMessage = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  padding: theme.spacing(2.5, 3),
  borderRadius: theme.spacing(2.5),
  maxWidth: '80%',
  boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
  position: 'relative',
  border: '1px solid rgba(230, 230, 240, 0.7)',
}));

const UserMessage = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: theme.spacing(2.5, 3),
  borderRadius: theme.spacing(2.5),
  maxWidth: '80%',
  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.2)',
  marginLeft: 'auto',
  position: 'relative',
}));

const SearchBox = styled(TextField)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(3),
  left: '50%',
  transform: 'translateX(-50%)',
  width: '90%',
  zIndex: 100,
  maxWidth: '850px',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.98)',
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(4),
    padding: theme.spacing(1.5, 2),
    fontSize: '1.1rem',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    '& fieldset': {
      borderColor: 'rgba(200, 200, 220, 0.3)',
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
  },
  '& .MuiInputBase-input': {
    padding: theme.spacing(1.5, 2),
    fontFamily: "'Space Grotesk', sans-serif",
  },
}));

// Add a styled component for loading indicator
const LoadingMessage = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginLeft: theme.spacing(6),
}));

// Add a styled button for starting the confidence rating
const RateConfidenceButton = styled(Button)(({ theme }) => ({
  textAlign: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1.5, 4),
  borderRadius: theme.spacing(3),
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '1.05rem',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.4)',
  width: 'auto',
  alignSelf: 'center',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.6)',
  },
  transition: 'all 0.3s ease',
}));

// Add a styled component for the confidence overlay
const ConfidenceOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(12px)',
  zIndex: 100,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  animation: 'fadeIn 0.4s ease-out',
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 }
  }
}));

const BackButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  backgroundColor: 'rgba(0, 0, 0, 0.05)',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.1)',
  },
}));

// Add a styled component for formatted message text
const FormattedMessage = styled('div')(({ theme }) => ({
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: '1.2rem',
  color: 'rgba(10, 10, 30, 0.85)',
  fontWeight: 500,
  lineHeight: 1.5,
  '& strong': {
    fontWeight: 700,
    color: 'rgba(0, 0, 0, 0.9)',
  },
  '& em': {
    fontStyle: 'italic',
  },
}));

export default function RightQuestion({ 
  selectedNPC,
  onQuestionSubmit,
  context = [], // Original reading context that doesn't change
  history = [], // Follow-up Q&A history that gets displayed
  initialStressLevel = null,
  finalStressLevel = null,
  onFinalStressChange = null,
}) {
  const [searchText, setSearchText] = useState('');
  const [conversation, setConversation] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confidenceRating, setConfidenceRating] = useState(initialStressLevel || 3);
  const [showRatingUI, setShowRatingUI] = useState(false);
  const theme = useTheme();
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const currentLang = useLanguage(); // 获取当前语言
  const t = languages[currentLang] || languages.en; // 获取翻译文本
  
  // Load confetti script from CDN
  useEffect(() => {
    if (!window.confetti) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/canvas-confetti@1.9.3/dist/confetti.browser.min.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  
  // Scroll to bottom when conversation updates
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [conversation]);

  // Initialize conversation with history
  useEffect(() => {
    // Start with the follow-up prompt
    const initialConversation = [];
    
    // console.log("RightQuestion - Received followUpHistory:", history);
    
    // Always add a prompt message when entering this view
    initialConversation.push({
      type: 'system',
      content: t.rightQuestion.followUpPrompt,
    });
    
    // If there's existing follow-up history, display it
    if (history && history.length > 0) {
      const followUpMessages = history.map(msg => ({
        type: msg.role,
        content: msg.content
      }));
      
      initialConversation.push(...followUpMessages);
    }
    
    console.log("RightQuestion - Displaying conversation:", initialConversation);
    setConversation(initialConversation);
  }, [history]);

  const handleSearchSubmit = async () => {
    if (!searchText.trim() || isLoading) return;
    
    setIsLoading(true);
    console.log("Sending follow-up question:", searchText);
    
    // Add user's question to conversation
    const newConversation = [
      ...conversation,
      { type: 'user', content: searchText }
    ];
    setConversation(newConversation);

    try {
      // Prepare the history to send - context + existing follow-up history + new question
      const historyToSend = [
        // First include the complete original reading context
        ...context.map(msg => ({ role: msg.role, content: msg.content })),
        // Then include existing follow-up history 
        ...history.map(msg => ({ role: msg.role, content: msg.content })),
        // Finally add the new question
        { role: 'user', content: searchText }
      ];
      
      // console.log("Sending combined context+history with length:", historyToSend.length);

      console.log("name",selectedNPC?.id)
      
      
      // Send the full context and history for complete understanding
      const response = await fetch('/followup' + (selectedNPC?.id ? `?character_id=${encodeURIComponent(selectedNPC.id)}` : ''), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          history: historyToSend
        })
      });
      
      const data = await response.json();
      
      if (data.answer) {
        // console.log("Received answer:", data.answer);
        
        // Format the response - convert markdown to HTML
        const formattedAnswer = convertMarkdownToHtml(data.answer);
        
        // Update conversation with assistant response
        const updatedConversation = [
          ...newConversation,
          { type: 'assistant', content: formattedAnswer }
        ];
        
        setConversation(updatedConversation);
        
        // Create updated history - only the follow-up Q&A, not the original context
        const updatedHistory = [
          ...history,
          { role: 'user', content: searchText },
          { role: 'assistant', content: data.answer } // Store original unformatted answer in history
        ];
        
        // console.log("Updated follow-up history:", updatedHistory);
        
        // Pass only the updated follow-up history back to parent
        onQuestionSubmit(searchText, updatedHistory);
      }
    } catch (error) {
      console.error('Error getting follow-up response:', error);
      setConversation(prev => [...prev, {
        type: 'assistant',
        content: t.rightQuestion.errorMessage
      }]);
    } finally {
      setIsLoading(false);
      setSearchText('');
    }
  };

  // Function to convert markdown formatting to HTML
  const convertMarkdownToHtml = (text) => {
    if (!text) return '';
    
    // Make a copy of the text to modify
    let formatted = text;
    console.log("formatted",formatted)
    
    // Bold: **text** -> <strong>text</strong>
    formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    
    // Italic: *text* -> <em>text</em>
    formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    
    // Process line breaks: \n\n -> <br/><br/>
    formatted = formatted.replace(/\n\n/g, '<br/><br/>');
    
    // Process single line breaks: \n -> <br/>
    formatted = formatted.replace(/\n/g, '<br/>');
    
    // Log the conversion for debugging
    // console.log('Markdown conversion:');
    // console.log('Original:', text);
    // console.log('Formatted:', formatted);
    
    return formatted;
  };

  const handleStressChange = (event, newValue) => {
    setConfidenceRating(newValue);
  };

  const handleStressSubmit = () => {
    if (onFinalStressChange) {
      onFinalStressChange(confidenceRating);
    }
    
    // Trigger confetti celebration effect
    if (window.confetti) {
      const colors = ['#1976d2']; // Default blue
      
      // Add colors based on confidence level
      if (confidenceRating >= 4) {
        // For higher confidence, add celebratory colors
        colors.push('#4caf50', '#ff9800');
      } else if (confidenceRating <= 2) {
        // For lower confidence, add subdued colors
        colors.push('#607d8b');
      }
      
      // Fire confetti from center-bottom of screen
      window.confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.7 },
        colors: colors,
        disableForReducedMotion: true
      });
    }
    
    setShowRatingUI(false);
    navigate('/readings');
  };

  const renderMessage = (message, index) => {
    if (message.type === 'system') {
      return (
        <Grow in={true} key={index} timeout={500}>
          <MessageGroup>
            <MessageAvatar src={selectedNPC?.avatar_image}>
              {!selectedNPC?.avatar_image && <ChatIcon />}
            </MessageAvatar>
            <ReaderMessage sx={{
              backgroundColor: 'rgba(250, 250, 252, 0.95)',
              borderRadius: '22px',
              borderTopLeftRadius: '4px',
            }}>
              <Typography 
                sx={{ 
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '1.2rem',
                  color: 'rgba(10, 10, 30, 0.85)',
                  fontWeight: 500,
                  lineHeight: 1.5,
                }}
              >
                {convertMarkdownToHtml(message.content)}
              </Typography>
            </ReaderMessage>
          </MessageGroup>
        </Grow>
      );
    } else if (message.type === 'user') {
      // Convert hex color to rgba with 0.15 opacity
      const hexToRgba = (hex, opacity) => {
        if (!hex) return theme.palette.primary.main;
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      };

      const backgroundColor = theme.palette.primary.main;

      return (
        <Grow in={true} key={index} timeout={500}>
          <MessageGroup sx={{ justifyContent: 'flex-end' }}>
            <UserMessage sx={{ 
              backgroundColor,
              borderRadius: '22px',
              borderTopRightRadius: '4px',
              color: theme.palette.primary.contrastText,
            }}>
              <Typography sx={{ 
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '1.2rem',
                color: 'rgba(255, 255, 255, 0.95)',
                fontWeight: 500,
                lineHeight: 1.5,
              }}>
                {convertMarkdownToHtml(message.content)}
              </Typography>
            </UserMessage>
          </MessageGroup>
        </Grow>
      );
    } else {
      return (
        <Grow in={true} key={index} timeout={500}>
          <MessageGroup>
            <MessageAvatar src={selectedNPC?.avatar_image}>
              {!selectedNPC?.avatar_image && <ChatIcon />}
            </MessageAvatar>
            <ReaderMessage sx={{
              backgroundColor: 'rgba(250, 250, 252, 0.95)',
              borderRadius: '22px',
              borderTopLeftRadius: '4px',
            }}>
              <FormattedMessage dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(message.content) }} />
            </ReaderMessage>
          </MessageGroup>
        </Grow>
      );
    }
  };

  // Render the confidence rating message in the conversation
  const renderRatingPrompt = () => {
    if (finalStressLevel === null && conversation.length > 1) {
      return (
        <Grow in={true} timeout={800}>
          <MessageGroup>
            
            <ReaderMessage sx={{
              backgroundColor: 'rgba(250, 250, 252, 0.95)',
              borderRadius: '22px',
              borderTopLeftRadius: '4px',
            }}>
              <Typography sx={{ 
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: '1.25rem',
                color: 'rgba(10, 10, 30, 0.85)',
                fontWeight: 500,
                lineHeight: 1.5,
                marginBottom: 2
              }}>
                {t.rightQuestion.conversationEffect}
              </Typography>
              
              <RateConfidenceButton 
                onClick={() => setShowRatingUI(true)}
              >
                {t.rightQuestion.rateYourFeelings}
              </RateConfidenceButton>
            </ReaderMessage>
          </MessageGroup>
        </Grow>
      );
    }
    return null;
  };

  return (
    <QuestionContainer>
      
      <QuestionBlock elevation={0}>
        <ConversationContainer ref={containerRef}>
          {conversation.map((message, index) => renderMessage(message, index))}
          
          {/* Add the confidence rating prompt after all messages */}
          {renderRatingPrompt()}
          
          {/* Loading indicator */}
          {isLoading && (
            <Fade in={true}>
              <LoadingMessage>
                <CircularProgress size={24} color="primary" />
                <Typography sx={{ 
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '1.1rem',
                  color: 'rgba(0, 0, 0, 0.6)',
                  fontWeight: 500,
                }}>
                  {t.rightQuestion.consultingCards}
                </Typography>
              </LoadingMessage>
            </Fade>
          )}
        </ConversationContainer>
      </QuestionBlock>
      
      <SearchBox
        fullWidth
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder={t.rightQuestion.placeholder}
        disabled={isLoading}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            handleSearchSubmit();
          }
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {isLoading ? (
                <CircularProgress size={24} color="primary" />
              ) : (
                <IconButton
                  onClick={handleSearchSubmit}
                  disabled={isLoading || !searchText.trim()}
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: 'white',
                    '&:hover': {
                      backgroundColor: theme.palette.primary.dark,
                    },
                    '&.Mui-disabled': {
                      backgroundColor: 'rgba(0, 0, 0, 0.12)',
                      color: 'rgba(0, 0, 0, 0.26)',
                    },
                    width: 40,
                    height: 40,
                  }}
                >
                  <ArrowForwardIcon />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />
      
      {/* Full-page confidence rating overlay */}
      {showRatingUI && (
        <ConfidenceOverlay>
          <BackButton onClick={() => setShowRatingUI(false)}>
            <ArrowBackIcon />
          </BackButton>
          
          <Typography variant="h4" sx={{ 
            fontFamily: "'Space Grotesk', sans-serif",
            mb: 6,
            textAlign: 'center',
            fontWeight: 600,
            color: 'rgba(10, 10, 30, 0.85)',
          }}>
            {t.rightQuestion.rateYourFeelings}
          </Typography>
          
          <Box sx={{ width: '100%', maxWidth: '600px' }}>
            <ConfidenceRating
              value={confidenceRating}
              onChange={handleStressChange}
              onSubmit={handleStressSubmit}
              // promptText="How confident do you feel now after our conversation?"
              buttonText={t.rightQuestion.submitRating}
            />
          </Box>
        </ConfidenceOverlay>
      )}
    </QuestionContainer>
  );
}
